import type FullCalendar from '@fullcalendar/react';
import { useRouter, useSearch } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStorage } from '../../../../hooks/useStorage';
import { useFlags } from '../../../../context/featureFlag';
import { usePlanContext } from '../../context/PlanContext';
import { useCrossTeamData } from '../../hooks/useCrossTeamData';
import type { RouterOutput } from '../../../../../trpc';

type CrossTeamResult = RouterOutput['plan']['crossTeamTracksForAthlete'][0];
import { CalendarWorkoutEvent } from '../hooks/useCalendarSync';

function clampDate(
  date: DateTime,
  minDate: DateTime,
  maxDate: DateTime,
): DateTime {
  if (!date.isValid) return minDate;
  if (date < minDate) return minDate;
  if (date > maxDate) return maxDate;
  return date;
}

type CalendarContextType = {
  navigateToDate: (date: DateTime) => void;
  calendarRef: React.RefObject<FullCalendar>;
  selectedTrackIds: string[];
  toggleTrackSelection: (trackId: string) => void;
  selectAllTracks: () => void;
  unselectAllTracks: () => void;
  selectedCrossTeamTrackIds: string[];
  // Cross-team tracks/blocks/events for the selected athlete, keyed on the
  // calendar's currentDate so they follow calendar navigation. Consumers
  // must read this (not PlanContext's timelineDate-keyed additionalTeamsData)
  // so the grid and sidebar never disagree.
  additionalTeamsData: CrossTeamResult[];
  allCrossTeamTrackIds: string[];
  toggleCrossTeamTrackSelection: (trackId: string) => void;
  selectAllCrossTeamTracks: () => void;
  unselectAllCrossTeamTracks: () => void;
  deselectedHubEventTypeIds: Set<string>;
  toggleHubEventTypeSelection: (eventTypeId: string) => void;
  selectAllHubEventTypes: () => void;
  unselectAllHubEventTypes: (eventTypeIds: string[]) => void;
  visibleRange: { start: string; end: string } | null;
  setVisibleRange: (range: { start: string; end: string } | null) => void;
  eventsByTrack: Map<string, CalendarWorkoutEvent[]>;
  // from the PlanContext
  minDate: DateTime;
  maxDate: DateTime;
  currentDate: DateTime;
  setCurrentDate: (date: DateTime) => void;
  miniCalendarDate: DateTime;
  setMiniCalendarDate: (date: DateTime) => void;
  workoutDates: Set<string>;
};

const CalendarContext = createContext<CalendarContextType | undefined>(
  undefined,
);

/*
 * This context is used to store the state and data used by the Calendar Page to keep the MainCalendar and MiniCalendar in sync.
 * We use useCalendarSync to fetch the workout events and use the selectedTrackIds to filter the workout events.
 */
export function CalendarProvider({ children }: { children: ReactNode }) {
  const {
    teamId,
    minDate,
    maxDate,
    filteredTracks,
    eventsByTrack,
    selectedAthlete,
    showAdditionalTeams,
  } = usePlanContext();
  const { showAdditionalTeamsFlag } = useFlags();
  const { navigate } = useRouter();
  const calendarRef = useRef<FullCalendar>(null);
  const searchParams = useSearch({ strict: false }) as {
    date?: string;
    athleteId?: string;
  };
  const parsedDate = searchParams.date
    ? DateTime.fromISO(searchParams.date)
    : DateTime.now();
  const initialDate = clampDate(parsedDate, minDate, maxDate);

  // Update URL if the initial date was clamped or invalid (only on mount)
  useEffect(() => {
    const isInvalid = !parsedDate.isValid;
    const wasClamped =
      isInvalid ||
      parsedDate.toFormat('yyyy-MM') !== initialDate.toFormat('yyyy-MM');

    if (wasClamped) {
      navigate({
        to: '/$teamId/plan/calendar',
        params: { teamId },
        search: { ...searchParams, date: initialDate.toFormat('yyyy-MM') },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentDate, setCurrentDate] = useState<DateTime>(initialDate);
  const [miniCalendarDate, setMiniCalendarDate] =
    useState<DateTime>(initialDate);

  // Keyed on the calendar's currentDate (not the timeline's date) so
  // cross-team events follow calendar navigation the same way the active
  // team's per-month TeamCoach_Calendar bootstrap does.
  const { additionalTeamsData } = useCrossTeamData({
    athleteProfileId: selectedAthlete?.id ?? null,
    enabled: showAdditionalTeams && showAdditionalTeamsFlag,
    timelineDate: currentDate,
    activeTeamId: teamId,
  });

  const [deselectedTrackIdsArray, setDeselectedTrackIdsArray] = useStorage<
    string[]
  >('calendar-filter:deselected-tracks', []);
  const [
    deselectedCrossTeamTrackIdsArray,
    setDeselectedCrossTeamTrackIdsArray,
  ] = useStorage<string[]>('calendar-filter:deselected-cross-team-tracks', []);
  const [deselectedHubEventTypeIdsArray, setDeselectedHubEventTypeIdsArray] =
    useStorage<string[]>('calendar-filter:deselected-hub-event-types', []);

  const deselectedTrackIds = useMemo(
    () => new Set(deselectedTrackIdsArray),
    [deselectedTrackIdsArray],
  );
  const deselectedCrossTeamTrackIds = useMemo(
    () => new Set(deselectedCrossTeamTrackIdsArray),
    [deselectedCrossTeamTrackIdsArray],
  );
  const allCrossTeamTrackIds = useMemo(
    () => additionalTeamsData.flatMap((team) => team.tracks.map((t) => t.id)),
    [additionalTeamsData],
  );
  const deselectedHubEventTypeIds = useMemo(
    () => new Set(deselectedHubEventTypeIdsArray),
    [deselectedHubEventTypeIdsArray],
  );
  const [visibleRange, setVisibleRange] = useState<{
    start: string;
    end: string;
  } | null>(null);

  const setCurrentDateClamp = useCallback(
    (date: DateTime) => {
      const targetDate = clampDate(date, minDate, maxDate);
      setCurrentDate(targetDate);
    },
    [minDate, maxDate],
  );

  const setMiniCalendarDateClamp = useCallback(
    (date: DateTime) => {
      const targetDate = clampDate(date, minDate, maxDate);
      setMiniCalendarDate(targetDate);
    },
    [minDate, maxDate],
  );

  const selectedTrackIds = useMemo(
    () =>
      filteredTracks
        .map((t) => t.id)
        .filter((id) => !deselectedTrackIds.has(id)),
    [filteredTracks, deselectedTrackIds],
  );

  const toggleTrackSelection = useCallback(
    (trackId: string) => {
      setDeselectedTrackIdsArray((prev) => {
        const prevSet = new Set(prev);
        if (prevSet.has(trackId)) {
          prevSet.delete(trackId);
        } else {
          prevSet.add(trackId);
        }
        return [...prevSet];
      });
    },
    [setDeselectedTrackIdsArray],
  );

  const selectAllTracks = useCallback(() => {
    setDeselectedTrackIdsArray([]);
  }, [setDeselectedTrackIdsArray]);

  const unselectAllTracks = useCallback(() => {
    setDeselectedTrackIdsArray(filteredTracks.map((track) => track.id));
  }, [filteredTracks, setDeselectedTrackIdsArray]);

  const selectedCrossTeamTrackIds = useMemo(
    () =>
      allCrossTeamTrackIds.filter((id) => !deselectedCrossTeamTrackIds.has(id)),
    [allCrossTeamTrackIds, deselectedCrossTeamTrackIds],
  );

  const toggleCrossTeamTrackSelection = useCallback(
    (trackId: string) => {
      setDeselectedCrossTeamTrackIdsArray((prev) => {
        const prevSet = new Set(prev);
        if (prevSet.has(trackId)) {
          prevSet.delete(trackId);
        } else {
          prevSet.add(trackId);
        }
        return [...prevSet];
      });
    },
    [setDeselectedCrossTeamTrackIdsArray],
  );

  const selectAllCrossTeamTracks = useCallback(() => {
    setDeselectedCrossTeamTrackIdsArray([]);
  }, [setDeselectedCrossTeamTrackIdsArray]);

  const unselectAllCrossTeamTracks = useCallback(() => {
    setDeselectedCrossTeamTrackIdsArray(allCrossTeamTrackIds);
  }, [allCrossTeamTrackIds, setDeselectedCrossTeamTrackIdsArray]);

  const toggleHubEventTypeSelection = useCallback(
    (eventTypeId: string) => {
      setDeselectedHubEventTypeIdsArray((prev) => {
        const prevSet = new Set(prev);
        if (prevSet.has(eventTypeId)) {
          prevSet.delete(eventTypeId);
        } else {
          prevSet.add(eventTypeId);
        }
        return [...prevSet];
      });
    },
    [setDeselectedHubEventTypeIdsArray],
  );

  const selectAllHubEventTypes = useCallback(() => {
    setDeselectedHubEventTypeIdsArray([]);
  }, [setDeselectedHubEventTypeIdsArray]);

  const unselectAllHubEventTypes = useCallback(
    (eventTypeIds: string[]) => {
      setDeselectedHubEventTypeIdsArray(eventTypeIds);
    },
    [setDeselectedHubEventTypeIdsArray],
  );

  // Collect all workout dates for selected tracks to show indicators in mini calendar
  const workoutDates = useMemo(() => {
    const selectedSet = new Set(selectedTrackIds);
    const dates = new Set<string>();
    for (const [trackId, trackEvents] of eventsByTrack) {
      if (!selectedSet.has(trackId)) continue;
      for (const event of trackEvents) {
        dates.add(event.assignedDate);
      }
    }
    return dates;
  }, [eventsByTrack, selectedTrackIds]);

  const navigateToDate = useCallback(
    (date: DateTime) => {
      const targetDate = clampDate(date, minDate, maxDate);

      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.gotoDate(targetDate.toJSDate());
      }
      setCurrentDate(targetDate);
      // Sync mini calendar to main calendar when navigating
      setMiniCalendarDate(targetDate);
      navigate({
        to: '/$teamId/plan/calendar',
        params: { teamId },
        search: { ...searchParams, date: targetDate.toFormat('yyyy-MM') },
        replace: false,
      });
    },
    [minDate, maxDate, navigate, teamId, searchParams],
  );

  const value = useMemo<CalendarContextType>(
    () => ({
      navigateToDate,
      calendarRef,
      selectedTrackIds,
      toggleTrackSelection,
      selectAllTracks,
      unselectAllTracks,
      selectedCrossTeamTrackIds,
      toggleCrossTeamTrackSelection,
      selectAllCrossTeamTracks,
      unselectAllCrossTeamTracks,
      additionalTeamsData,
      allCrossTeamTrackIds,
      deselectedHubEventTypeIds,
      toggleHubEventTypeSelection,
      selectAllHubEventTypes,
      unselectAllHubEventTypes,
      visibleRange,
      setVisibleRange,
      eventsByTrack,
      minDate,
      maxDate,
      currentDate,
      setCurrentDate: setCurrentDateClamp,
      miniCalendarDate,
      setMiniCalendarDate: setMiniCalendarDateClamp,
      workoutDates,
    }),
    [
      navigateToDate,
      selectedTrackIds,
      toggleTrackSelection,
      selectAllTracks,
      unselectAllTracks,
      selectedCrossTeamTrackIds,
      toggleCrossTeamTrackSelection,
      selectAllCrossTeamTracks,
      unselectAllCrossTeamTracks,
      additionalTeamsData,
      allCrossTeamTrackIds,
      deselectedHubEventTypeIds,
      toggleHubEventTypeSelection,
      selectAllHubEventTypes,
      unselectAllHubEventTypes,
      visibleRange,
      setVisibleRange,
      eventsByTrack,
      minDate,
      maxDate,
      currentDate,
      setCurrentDateClamp,
      miniCalendarDate,
      setMiniCalendarDateClamp,
      workoutDates,
    ],
  );

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}

export function useCalendarContext() {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error(
      'useCalendarContext must be used within a CalendarProvider',
    );
  }
  return context;
}
